import React, { useMemo, useState } from "react";
import { LinearProgress } from "@mui/material";
import { cn } from "@helpers/utils";
import { BaseCollapseProps, BaseCollapseContextValue } from "./types";
import { BaseCollapseContext } from "./context";
import { BaseCollapseHeader } from "./BaseCollapseHeader";
import { BaseCollapseBody } from "./BaseCollapseBody";
import { ChevronDown } from "lucide-react";
import { useBaseCollapseContext } from "./context";

/**
 * BaseCollapse - A styled collapse component with consistent styling
 * for collapsible content sections with a custom header.
 *
 * Provides a bordered container with rounded corners, white background,
 * hover effects, and smooth collapse animations.
 *
 * Usage:
 *    <BaseCollapse isOpen={open}>
 *      <BaseCollapse.Header>Header</BaseCollapse.Header>
 *      <BaseCollapse.Body>Content</BaseCollapse.Body>
 *    </BaseCollapse>
 */
const BaseCollapse = (props: BaseCollapseProps) => {
    const {
        isOpen: controlledIsOpen,
        onOpenChange,
        children,
        className,
        loading,
        defaultOpen = false,
    } = props;

    // Use internal state if isOpen is not provided
    const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

    const setIsOpen = (open: boolean) => {
        if (isControlled) {
            onOpenChange?.(open);
        } else {
            setInternalIsOpen(open);
        }
    };

    const contextValue = useMemo<BaseCollapseContextValue>(
        () => ({
            isOpen,
            setIsOpen,
            loading,
        }),
        [isOpen, setIsOpen, loading]
    );

    return (
        <BaseCollapseContext.Provider value={contextValue}>
            <div
                className={cn(
                    "border-[1px] border-solid rounded-lg bg-white transition-all overflow-hidden relative",
                    "hover:border-inputs-border-focused",
                    isOpen ? "border-inputs-border-focused" : "border-inputs-border-default",
                    loading && "opacity-60 pointer-events-none",
                    className
                )}
            >
                {loading && (
                    <div className="absolute top-0 left-0 w-full h-1 z-50">
                        <LinearProgress sx={{ height: "100%" }} />
                    </div>
                )}
                {children}
            </div>
        </BaseCollapseContext.Provider>
    );
};

const BaseCollapseChevron = () => {
    const { isOpen } = useBaseCollapseContext();
    return (
        <ChevronDown
            className="w-5 h-5 text-gray-600"
            style={{
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 200ms",
            }}
        />
    );
};

// Attach subcomponents for composable API
BaseCollapse.Header = BaseCollapseHeader;
BaseCollapse.Body = BaseCollapseBody;
BaseCollapse.Chevron = BaseCollapseChevron;

export default BaseCollapse;
